import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import FormCard from "../../Common/FormCard";
import InputField from "../../Common/InputField";
import {
  fetchDropdownData,
  clearSelectedMapping,
  createUpdateShiftMapping,
  fetchAvailableEmployees,
  clearAvailableEmployees,
  fetchBookedShifts,
  clearBookedShifts,
  fetchMappingList,
} from "../../API_Service/AssociateShiftMapping_Slice";
import { showToast } from "../../Common/Toast";
import {
  Dropdown,
  ConfirmDialog,
  Button,
  hasPrivilege,
  PRIVILEGES,
} from "../../Common/UIComponents";
import CreateEditUserPage from "../UserManagement/CreateEditUserPage";
import FacilityConfigCreate from "../ApplicationConfig/Facility/FacilityConfigCreate";
import {
  ShiftDrawer,
  getShiftName,
  calcDurationHrs,
  SHIFT_NAMES,
} from "../ApplicationConfig/Shift/ShiftConfigIndex";
import { saveShifts, fetchShifts } from "../../API_Service/Settings_Slice";

// Convert "HH:MM:SS" or "HH:MM" 24-hour string to "hh:mm AM/PM"
const to12h = (t) => {
  if (!t) return "";
  const [hStr, mStr] = String(t).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return t;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// EMPLOYEE CREATE MODAL â€” wraps the full Associate create page
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const QuickAddEmployeeModal = ({ isOpen, onClose, onCreated }) => {
  if (!isOpen) return null;
  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[210] overflow-y-auto">
        <div className="min-h-full flex items-start justify-center p-4">
          <div
            className="bg-[#f7fafa] rounded-2xl shadow-2xl w-full max-w-6xl my-4 overflow-hidden relative"
            style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={onClose}
                title="Close"
                className="h-9 w-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-[#08757C] hover:border-[#08757C]/40 transition-all"
              >
                <span className="material-symbols-outlined text-[18px]">
                  close
                </span>
              </button>
            </div>
            <CreateEditUserPage
              embedded
              onCancel={onClose}
              onSuccess={(newId, newName) => {
                onCreated?.(newId, newName);
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const CreateEditShiftMappingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.CompanyId || localStorage.getItem("companyId");
  const userId = user?.EmployeeId || localStorage.getItem("empid");
  const {
    facilities,
    shifts,
    selectedMapping,
    holidays,
    availableEmployees,
    employeesLoading,
    bookedShiftIds,
    bookedShiftsLoading,
    list: mappingsList,
  } = useSelector((state) => state.shiftMapping);

  const [formData, setFormData] = useState({
    facilityId: "",
    facilityName: "",
    employeeId: "",
    employeeName: "",
    shiftId: "",
    shiftName: "",
    shiftStartTime: "",
    shiftEndTime: "",
    recurrenceType: "weekly",
    fromDate: "",
    toDate: "",
    selectedDates: [],
    isActive: true,
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [conflictError, setConflictError] = useState(null);
  const [employeeConflictDialog, setEmployeeConflictDialog] = useState({
    open: false,
  });
  const [successMsg, setSuccessMsg] = useState();

  // Inline create modals - keep user's typed mapping values intact
  const [showAddShiftModal, setShowAddShiftModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);

  // Refresh facilities dropdown after adding a new facility; auto-select it
  const refreshFacilitiesAndSelect = async (newFacilityId, facilityName) => {
    await dispatch(
      fetchDropdownData({ companyId, term: "FACILITIES", mappingId: 0 }),
    );
    if (newFacilityId) {
      setSuccessMsg(`Facility "${facilityName}" created successfully.`);
      setTimeout(() => setSuccessMsg(""), 4000);
      setTimeout(() => handleFacilityChange(String(newFacilityId)), 100);
    }
  };

  // ── Local state for the embedded ShiftDrawer ──
  const [shiftSaving, setShiftSaving] = useState(false);
  const [shiftSaveError, setShiftSaveError] = useState(null);
  const [existingShiftRows, setExistingShiftRows] = useState([]);

  // When the drawer opens we need fresh raw shift rows for overlap validation.
  useEffect(() => {
    if (!showAddShiftModal) return;
    dispatch(fetchShifts({ ID: 0, companyID: companyId })).then((res) => {
      const list = res?.payload?.data ?? res?.payload ?? [];
      setExistingShiftRows(
        (Array.isArray(list) ? list : []).map((item, i) => ({
          id: item.Shift_Timing_Id ?? `row-${i}`,
          shiftNo: item.Shift_Num ?? i + 1,
          shiftName: item.Shift_Name ?? `Shift ${i + 1}`,
          startTime: (item.Shift_Start_Time ?? "").substring(0, 5),
          endTime: (item.Shift_End_Time ?? "").substring(0, 5),
        })),
      );
    });
  }, [showAddShiftModal, dispatch, companyId]);

  // Same payload format as ShiftConfigIndex's handleSave
  const handleShiftDrawerSave = (shiftForms, id, existingShiftCount) => {
    setShiftSaving(true);
    setShiftSaveError(null);
    const isEdit = !!id;
    const payload = {
      CompanyID: companyId,
      CreatedBy: parseInt(userId) || 1,
      Shifts: shiftForms.map((s, i) => ({
        ShiftTimingId: isEdit ? id : (s.id ?? 0),
        ShiftNo: isEdit
          ? (s._shiftNo ?? existingShiftCount + i + 1)
          : existingShiftCount + i + 1,
        ShiftName: getShiftName(
          isEdit
            ? Math.max(0, SHIFT_NAMES.indexOf(s.shiftName))
            : existingShiftCount + i,
        ),
        ShiftStartTime: s.startTime ? `${s.startTime.slice(0, 5)}:00` : null,
        ShiftEndTime: s.endTime ? `${s.endTime.slice(0, 5)}:00` : null,
        ShiftDuration: calcDurationHrs(s.startTime, s.endTime),
      })),
    };

    dispatch(saveShifts(payload))
      .then(async (res) => {
        const result = res?.payload?.data ?? res?.payload;
        if (Number(result) > 0) {
          await dispatch(
            fetchDropdownData({ companyId, term: "SHIFT", mappingId: 0 }),
          );
          setSuccessMsg("Shift created successfully.");
          setTimeout(() => setSuccessMsg(""), 4000);
          setShowAddShiftModal(false);
          // Auto-select the newly created shift
          setTimeout(() => {
            handleShiftChange({ target: { value: String(result) } });
          }, 100);
        } else {
          setShiftSaveError("Failed to save. Please try again.");
        }
      })
      .catch((err) => setShiftSaveError(err?.message ?? "Failed to save shift"))
      .finally(() => setShiftSaving(false));
  };

  // Last end time of existing shifts — used by drawer to pre-fill next start
  const lastShiftEndTime =
    existingShiftRows.length > 0
      ? existingShiftRows[existingShiftRows.length - 1].endTime
      : "";

  // Refresh employees for the current facility/shift/date selection and auto-select
  const refreshEmployeesAndSelect = async (newEmpId, empName) => {
    if (newEmpId) {
      setSuccessMsg(`Employee created successfully.`);
      setTimeout(() => setSuccessMsg(""), 4000);
    }
    if (
      !formData.facilityId ||
      !formData.shiftId ||
      formData.selectedDates.length === 0
    )
      return;
    const fromDate = formData.selectedDates[0];
    const toDate = formData.selectedDates[formData.selectedDates.length - 1];
    await dispatch(
      fetchAvailableEmployees({
        companyId,
        facilityId: formData.facilityId,
        shiftId: formData.shiftId,
        fromDate,
        toDate,
        excludeId: isEditMode ? parseInt(id) : 0,
      }),
    );
    if (newEmpId) {
      setTimeout(() => handleEmployeeChange(String(newEmpId)), 100);
    }
  };

  const isEditMode = !!(id && id !== "create");
  const isInactiveEdit = isEditMode && !formData.isActive;
  const shiftDetailsRef = useRef(null);

  const recurrenceOptions = [
    { type: "daily", label: "Daily", icon: "today" },
    { type: "weekly", label: "Weekly", icon: "calendar_view_week" },
    { type: "monthly", label: "Monthly", icon: "calendar_month" },
  ];

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYMD = (dateValue) => {
    if (!dateValue) return null;

    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    if (typeof dateValue === "string") {
      return dateValue.split("T")[0];
    }

    return null;
  };

  // Check if date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Check if date is a holiday
  const isHoliday = (date) => {
    if (!date || !holidays || holidays.length === 0) return false;

    const dateStr = formatDateToYMD(date);
    if (!dateStr) return false;

    return holidays.some((holiday) => {
      let fromDate = holiday.HolidayFromDate;
      let toDate = holiday.HolidayToDate;

      if (fromDate) fromDate = fromDate.split("T")[0];
      if (toDate) toDate = toDate.split("T")[0];

      if (!fromDate) return false;

      if (!toDate || fromDate === toDate) {
        return dateStr === fromDate;
      }

      return dateStr >= fromDate && dateStr <= toDate;
    });
  };

  // Check if date is disabled (weekend or holiday)
  const isDateDisabled = (date) => {
    return isWeekend(date) || isHoliday(date);
  };

  // Get disabled reason for tooltip
  const getDisabledReason = (date) => {
    if (isWeekend(date)) return "Weekends are not allowed";
    if (isHoliday(date)) return "Holiday - not available for shift mapping";
    return "";
  };

  // Fetch dropdown data for create mode
  useEffect(() => {
    if (companyId && (!id || id === "create")) {
      dispatch(
        fetchDropdownData({ companyId, term: "FACILITIES", mappingId: 0 }),
      );
      dispatch(fetchDropdownData({ companyId, term: "SHIFT", mappingId: 0 }));
      dispatch(fetchDropdownData({ companyId, term: "HOLIDAY", mappingId: 0 }));
    }
  }, [companyId, id]);

  // Fetch data for edit mode
  useEffect(() => {
    if (id && id !== "create" && companyId) {
      dispatch(
        fetchDropdownData({
          companyId,
          term: "SHIFT",
          mappingId: parseInt(id),
        }),
      );
    }
  }, [id, companyId]);

  // Fetch mapping list for conflict detection in create mode
  useEffect(() => {
    if (companyId && (!id || id === "create")) {
      dispatch(fetchMappingList(companyId));
    }
  }, [companyId, id, dispatch]);

  // Populate form when editing
  useEffect(() => {
    if (selectedMapping && id && id !== "create") {
      const selectedFacility = facilities.find(
        (f) => f.value === selectedMapping.FacilityId,
      );
      const selectedShift = shifts.find(
        (s) => s.value === selectedMapping.ShiftId,
      );
      const currentUserId = parseInt(userId);
      const selectedDatesArray = [];
      if (selectedMapping.FromDate && selectedMapping.ToDate) {
        const start = new Date(selectedMapping.FromDate);
        const end = new Date(selectedMapping.ToDate);
        const currentDate = new Date(start);

        while (currentDate <= end) {
          if (!isDateDisabled(currentDate)) {
            selectedDatesArray.push(formatDateToYMD(currentDate));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      setFormData({
        facilityId: selectedMapping.FacilityId || "",
        facilityName: selectedFacility?.label || "",
        employeeId: selectedMapping.EmployeeId || "",
        employeeName: "",
        shiftId: selectedMapping.ShiftId || "",
        shiftName: selectedShift?.Shift_Name || "",
        shiftStartTime: selectedShift?.Shift_Start_Time || "",
        shiftEndTime: selectedShift?.Shift_End_Time || "",
        recurrenceType:
          selectedMapping.RecurrenceType?.toLowerCase() || "weekly",
        fromDate: selectedMapping.FromDate?.split("T")[0] || "",
        toDate: selectedMapping.ToDate?.split("T")[0] || "",
        selectedDates: selectedDatesArray,
        isActive: selectedMapping.Status,
        CreatedBy: currentUserId,
      });

      if (selectedMapping.FromDate) {
        setCurrentMonth(new Date(selectedMapping.FromDate));
      }
    }
  }, [selectedMapping, facilities, shifts, holidays, id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSelectedMapping());
      dispatch(clearAvailableEmployees());
      dispatch(clearBookedShifts());
    };
  }, [dispatch]);

  // Debounced fetch of booked shifts whenever dates + facility are set
  useEffect(() => {
    if (!formData.facilityId || formData.selectedDates.length === 0) {
      dispatch(clearBookedShifts());
      return;
    }
    const fromDate = formData.selectedDates[0];
    const toDate = formData.selectedDates[formData.selectedDates.length - 1];
    const timer = setTimeout(() => {
      dispatch(
        fetchBookedShifts({
          companyId,
          facilityId: formData.facilityId,
          fromDate,
          toDate,
          excludeId: isEditMode ? parseInt(id) : 0,
        }),
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.selectedDates, formData.facilityId]);

  // Auto-reset shift if it becomes booked after a date/facility change
  useEffect(() => {
    if (
      formData.shiftId &&
      bookedShiftIds.includes(parseInt(formData.shiftId))
    ) {
      setFormData((prev) => ({
        ...prev,
        shiftId: "",
        shiftName: "",
        shiftStartTime: "",
        shiftEndTime: "",
        employeeId: "",
        employeeName: "",
      }));
      dispatch(clearAvailableEmployees());
    }
  }, [bookedShiftIds]);

  // Debounced fetch of available employees whenever dates + facility + shift are all set
  useEffect(() => {
    if (
      !formData.shiftId ||
      !formData.facilityId ||
      formData.selectedDates.length === 0
    ) {
      dispatch(clearAvailableEmployees());
      return;
    }
    const fromDate = formData.selectedDates[0];
    const toDate = formData.selectedDates[formData.selectedDates.length - 1];
    const timer = setTimeout(() => {
      dispatch(
        fetchAvailableEmployees({
          companyId,
          facilityId: formData.facilityId,
          shiftId: formData.shiftId,
          fromDate,
          toDate,
          excludeId: isEditMode ? parseInt(id) : 0,
        }),
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.selectedDates, formData.facilityId, formData.shiftId]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const handleFacilityChange = (facilityId) => {
    const selectedFacility = facilities.find(
      (f) => String(f.value) === String(facilityId),
    );
    setFormData((prev) => ({
      ...prev,
      facilityId,
      facilityName: selectedFacility?.label || "",
      shiftId: "",
      shiftName: "",
      shiftStartTime: "",
      shiftEndTime: "",
      employeeId: "",
      employeeName: "",
    }));
    dispatch(clearAvailableEmployees());
    dispatch(clearBookedShifts());
    if (errors.facilityId) setErrors((prev) => ({ ...prev, facilityId: "" }));
    setConflictError(null);
  };

  const handleEmployeeChange = (employeeId) => {
    const selectedEmployee = availableEmployees.find(
      (emp) => String(emp.value) === String(employeeId),
    );

    // Check if employee already has mappings for the same facility on any selected date
    if (
      !isEditMode &&
      formData.facilityId &&
      formData.selectedDates.length > 0 &&
      mappingsList &&
      mappingsList.length > 0
    ) {
      const employeeName = selectedEmployee?.label || "";
      const facilityName = formData.facilityName || "";

      console.log("Checking conflict for:", {
        employeeName,
        facilityName,
        selectedDates: formData.selectedDates,
      });
      console.log("Mappings list:", mappingsList);

      const conflictingShifts = mappingsList.filter((mapping) => {
        const mappingEmployeeName = String(mapping.EmployeeName || "").trim();
        const employeeNameTrimmed = String(employeeName).trim();

        const isSameEmployee = mappingEmployeeName === employeeNameTrimmed;

        if (!isSameEmployee) return false;

        // Check if any selected date falls within the mapping's date range
        const mappingFromDate = new Date(mapping.FromDate);
        const mappingToDate = new Date(mapping.ToDate);

        return formData.selectedDates.some((dateStr) => {
          const [year, month, day] = dateStr.split("-");
          const selectedDate = new Date(year, parseInt(month) - 1, day);
          return (
            selectedDate >= mappingFromDate && selectedDate <= mappingToDate
          );
        });
      });

      console.log("Conflicting shifts found:", conflictingShifts.length);

      if (conflictingShifts.length > 0) {
        const conflictingShiftNames = [
          ...new Set(conflictingShifts.map((s) => s.Shift_Name)),
        ].join(", ");
        const currentShiftName = formData.shiftName || "";
        setEmployeeConflictDialog({
          open: true,
          employeeName: employeeName,
          facilityName: facilityName,
          conflictingShiftNames: conflictingShiftNames,
          currentShiftName: currentShiftName,
          shiftCount: conflictingShifts.length,
          pendingEmployeeId: employeeId,
        });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      employeeId: employeeId,
      employeeName: selectedEmployee?.label || "",
    }));
    if (errors.employeeId) setErrors((prev) => ({ ...prev, employeeId: "" }));
    setConflictError(null);
  };

  const handleConfirmEmployeeConflict = () => {
    const { pendingEmployeeId } = employeeConflictDialog;
    const selectedEmployee = availableEmployees.find(
      (emp) => String(emp.value) === String(pendingEmployeeId),
    );
    setFormData((prev) => ({
      ...prev,
      employeeId: pendingEmployeeId,
      employeeName: selectedEmployee?.label || "",
    }));
    setEmployeeConflictDialog({ open: false });
  };

  const handleCancelEmployeeConflict = () => {
    setEmployeeConflictDialog({ open: false });
  };

  const handleShiftChange = (e) => {
    const shiftId = e.target.value;
    const selectedShift = shifts.find((s) => s.value === parseInt(shiftId));
    setFormData((prev) => ({
      ...prev,
      shiftId,
      shiftName: selectedShift?.Shift_Name || "",
      shiftStartTime: selectedShift?.Shift_Start_Time || "",
      shiftEndTime: selectedShift?.Shift_End_Time || "",
      employeeId: "",
      employeeName: "",
    }));
    if (errors.shiftId) setErrors((prev) => ({ ...prev, shiftId: "" }));
    setConflictError(null);
  };

  const handleRecurrenceChange = (recurrenceType) => {
    setFormData((prev) => ({ ...prev, recurrenceType, selectedDates: [] }));
    setConflictError(null);
  };

  const scrollToShiftDetails = () => {
    setTimeout(
      () =>
        shiftDetailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      50,
    );
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    if (isDateDisabled(date)) return;

    const dateStr = formatDateToYMD(date);
    const wasEmpty = formData.selectedDates.length === 0;
    setFormData((prev) => {
      if (prev.selectedDates.includes(dateStr)) {
        return {
          ...prev,
          selectedDates: prev.selectedDates.filter((d) => d !== dateStr).sort(),
        };
      }
      return {
        ...prev,
        selectedDates: [...prev.selectedDates, dateStr].sort(),
      };
    });
    if (wasEmpty) scrollToShiftDetails();
    setConflictError(null);
  };

  useEffect(() => {
    if (formData.selectedDates.length > 0 && errors.selectedDates) {
      setErrors((prev) => ({
        ...prev,
        selectedDates: "",
      }));
    }
  }, [formData.selectedDates]);

  const handleWeekSelect = (weekDates) => {
    const validWeekDates = weekDates.filter(
      (d) => d !== null && !isDateDisabled(d),
    );
    const weekDateStrs = validWeekDates.map((d) => formatDateToYMD(d));

    const wasEmpty = formData.selectedDates.length === 0;
    setFormData((prev) => {
      const allSelected = weekDateStrs.every((date) =>
        prev.selectedDates.includes(date),
      );
      if (allSelected) {
        return {
          ...prev,
          selectedDates: prev.selectedDates
            .filter((d) => !weekDateStrs.includes(d))
            .sort(),
        };
      }
      return {
        ...prev,
        selectedDates: [
          ...new Set([...prev.selectedDates, ...weekDateStrs]),
        ].sort(),
      };
    });
    if (wasEmpty) scrollToShiftDetails();
    setConflictError(null);
  };

  const handleMonthSelect = () => {
    const daysInMonth = getDaysInMonth(currentMonth).filter(
      (d) => d !== null && !isDateDisabled(d),
    );
    const monthDateStrs = daysInMonth.map((d) => formatDateToYMD(d));

    const wasEmpty = formData.selectedDates.length === 0;
    setFormData((prev) => {
      const allSelected = monthDateStrs.every((date) =>
        prev.selectedDates.includes(date),
      );
      if (allSelected) {
        return {
          ...prev,
          selectedDates: prev.selectedDates
            .filter((d) => !monthDateStrs.includes(d))
            .sort(),
        };
      }
      return {
        ...prev,
        selectedDates: [
          ...new Set([...prev.selectedDates, ...monthDateStrs]),
        ].sort(),
      };
    });
    if (wasEmpty) scrollToShiftDetails();
    setConflictError(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.facilityId) newErrors.facilityId = "Please select a facility";
    if (!formData.employeeId)
      newErrors.employeeId = "Please select an employee";
    if (!formData.shiftId) newErrors.shiftId = "Please select a shift";
    if (formData.selectedDates.length === 0) {
      newErrors.selectedDates = "Please select at least one date";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    debugger;
    if (!validateForm()) return;

    setSaving(true);
    setConflictError(null);

    try {
      const isEdit = id && id !== "create";
      const fromDate = formData.selectedDates[0];
      const toDate = formData.selectedDates[formData.selectedDates.length - 1];
      const existsApplicationCode = "LBB";

      const shiftData = {
        AssociateShiftMappingId: isEdit ? parseInt(id) : 0,
        FacilityId: parseInt(formData.facilityId),
        ShiftId: parseInt(formData.shiftId),
        EmployeeId: parseInt(formData.employeeId),
        CompanyId: companyId,
        Status: formData.isActive ? 1 : 0,
        FromDate: fromDate,
        ToDate: toDate,
        ExistsApplicationCode: existsApplicationCode,
        CreatedBy: parseInt(userId),
        RecurrenceType: formData.recurrenceType.toUpperCase(),
      };

      const result = await dispatch(
        createUpdateShiftMapping({ shiftData, companyId }),
      ).unwrap();

      console.log("Processed API Response:", result);

      // Check if the operation was successful
      if (result.success === true) {
        const successMessage = isEdit
          ? "Shift mapping updated successfully!"
          : "Shift mapping created successfully!";

        // Navigate with success state
        navigate("/shift-mapping", {
          state: {
            refresh: true,
            successMessage: successMessage,
            success: true,
          },
        });
      }
      // Check for specific conflict scenarios
      else if (result.success === false) {
        // Handle different conflict messages
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        if (result.message?.includes("adjacent")) {
          setConflictError(
            "The selected dates are immediately next to an existing shift assignment for this employee. Please choose a date range that does not touch or overlap with existing mappings.",
          );
          showToast(
            "Dates overlap with an existing shift assignment",
            "warning",
          );
        } else if (result.conflictDates) {
          const conflictDatesList = result.conflictDates.split(",").join(", ");
          setConflictError(
            `The following dates are already booked: ${conflictDatesList}`,
          );
          showToast(
            "Some selected dates are already assigned to this employee",
            "warning",
          );
        } else {
          setConflictError(
            result.message ||
              "The selected dates conflict with existing mappings. Please choose different dates.",
          );
          showToast(result.message || "Mapping conflict detected", "warning");
        }
      }
    } catch (error) {
      console.error("Failed to save shift mapping:", error);
      const isOffline = error?.isOffline === true;
      // Handle rejected thunk errors
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "An unexpected error occurred while saving";
      if (isOffline) {
        // don't show it as a "conflict" at all
        showToast(errorMessage, "error");
      } else {
        setConflictError(errorMessage);
        showToast(errorMessage, "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weeks = [];

  for (let i = 0; i < daysInMonth.length; i += 7) {
    weeks.push(daysInMonth.slice(i, i + 7));
  }

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  return (
    <DashboardLayout>
      <div className="px-8 py-4 flex-1 w-full">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-3 gap-4">
          <div>
            <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-1.5">
              <span
                onClick={() => navigate("/shift-mapping")}
                className="cursor-pointer hover:text-[#08757c] transition-colors"
              >
                Shift Mapping
              </span>
              <span className="material-symbols-outlined text-[14px]">
                chevron_right
              </span>
              <span className="text-[#08757c] font-semibold">
                {id && id !== "create" ? "Edit Mapping" : "Add New Mapping"}
              </span>
            </nav>
            <h2 className="text-3xl font-extrabold text-primary tracking-tight">
              {id && id !== "create"
                ? "Edit Shift Assignment"
                : "Create Shift Assignment"}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/shift-mapping")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
            >
              <span className="material-symbols-outlined text-lg">
                arrow_back
              </span>
              Back to List
            </button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              <span className="material-symbols-outlined text-lg">
                {saving ? "hourglass_empty" : "save"}
              </span>
              {saving
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update Mapping"
                  : "Save Mapping"}
            </Button>
          </div>
        </div>

        {/* Conflict Error Display */}
        {conflictError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">
                error
              </span>
              <span className="font-semibold">Conflict Detected</span>
            </div>
            <p className="mt-1">{conflictError}</p>
          </div>
        )}

        {/* Success Message Display - Floating Notification */}
        {successMsg && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[9999] px-6 py-4 bg-lime-400 rounded-full text-black text-sm flex items-center gap-3 shadow-lg max-w-md">
            <span className="material-symbols-outlined text-[20px] text-black">
              check_circle
            </span>
            <span className="font-semibold">{successMsg}</span>
          </div>
        )}

        {/* Form Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-12 lg:col-span-8 space-y-3">
            {/* Recurrence Settings Card - FIRST */}
            <FormCard icon="repeat" title="Recurrence Settings">
              {/* Recurrence Type Radio Buttons */}
              <div className="mb-4">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">
                  Recurrence Pattern <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  {recurrenceOptions.map((option) => (
                    <label
                      key={option.type}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="recurrenceType"
                        value={option.type}
                        checked={formData.recurrenceType === option.type}
                        onChange={() => handleRecurrenceChange(option.type)}
                        disabled={isInactiveEdit}
                        className="w-4 h-4 text-primary accent-primary disabled:cursor-not-allowed"
                      />
                      <span className="flex items-center gap-1 text-sm text-slate-700">
                        <span className="material-symbols-outlined text-lg">
                          {option.icon}
                        </span>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Calendar View */}
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                {/* Calendar Header */}
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={prevMonth}
                    className="p-1 hover:bg-slate-100 rounded-lg"
                  >
                    <span className="material-symbols-outlined text-slate-600">
                      chevron_left
                    </span>
                  </button>
                  <span className="font-semibold text-sm text-slate-700">
                    {currentMonth.toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    onClick={nextMonth}
                    className="p-1 hover:bg-slate-100 rounded-lg"
                  >
                    <span className="material-symbols-outlined text-slate-600">
                      chevron_right
                    </span>
                  </button>
                </div>

                {/* Week Days Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-medium text-slate-500 py-1"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="space-y-1">
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 gap-1">
                      {week.map((date, dayIndex) => {
                        if (!date)
                          return <div key={dayIndex} className="h-8"></div>;

                        const dateStr = formatDateToYMD(date);
                        const isSelected =
                          formData.selectedDates.includes(dateStr);
                        const isToday = dateStr === formatDateToYMD(new Date());
                        const isDisabled = isDateDisabled(date);
                        const disabledReason = getDisabledReason(date);

                        return (
                          <button
                            key={dayIndex}
                            onClick={() => handleDateSelect(date)}
                            disabled={isDisabled || isInactiveEdit}
                            className={`h-8 rounded-lg text-xs font-medium transition-colors ${
                              isDisabled
                                ? "bg-slate-100 text-slate-300 cursor-not-allowed line-through"
                                : isInactiveEdit
                                  ? "bg-slate-50 text-slate-300 cursor-not-allowed"
                                  : isSelected
                                    ? "bg-primary text-white"
                                    : isToday
                                      ? "bg-primary/10 text-primary"
                                      : "hover:bg-slate-100 text-slate-700"
                            }`}
                            title={
                              isInactiveEdit
                                ? "Activate mapping to edit dates"
                                : disabledReason
                            }
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Week Selection Buttons */}
                {formData.recurrenceType === "weekly" && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">
                      Quick Select Week:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {weeks.map((week, weekIndex) => {
                        const weekDates = week.filter(
                          (d) => d !== null && !isDateDisabled(d),
                        );
                        if (weekDates.length === 0) return null;
                        const weekStart = weekDates[0];
                        const weekEnd = weekDates[weekDates.length - 1];
                        const isWeekSelected = weekDates.every((d) =>
                          formData.selectedDates.includes(formatDateToYMD(d)),
                        );
                        return (
                          <button
                            key={weekIndex}
                            type="button"
                            onClick={() => handleWeekSelect(weekDates)}
                            disabled={isInactiveEdit}
                            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              isWeekSelected
                                ? "bg-primary text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            Week {weekIndex + 1}
                            <span className="text-[10px] ml-1">
                              ({weekStart?.getDate()}-{weekEnd?.getDate()})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Month Selection Button */}
                {formData.recurrenceType === "monthly" && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={handleMonthSelect}
                      disabled={isInactiveEdit}
                      className={`w-full py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        formData.selectedDates.length ===
                        daysInMonth.filter(
                          (d) => d !== null && !isDateDisabled(d),
                        ).length
                          ? "bg-primary text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {formData.selectedDates.length ===
                      daysInMonth.filter(
                        (d) => d !== null && !isDateDisabled(d),
                      ).length
                        ? "âœ“ Full Month Selected (Excluding Weekends & Holidays)"
                        : "Select Full Month (Excluding Weekends & Holidays)"}
                    </button>
                  </div>
                )}

                {errors.selectedDates && (
                  <p className="text-xs text-red-500 mt-3">
                    {errors.selectedDates}
                  </p>
                )}

                {/* Selected Dates Summary */}
                {formData.selectedDates.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500">
                      Selected: {formData.selectedDates.length} date(s)
                    </p>
                  </div>
                )}
              </div>
            </FormCard>

            {/* Shift Details Card - AFTER calendar */}
            <div ref={shiftDetailsRef}>
              <FormCard icon="schedule" title="Shift Details">
                <div className="space-y-4">
                  {/* Facility */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                        Facility Name <span className="text-red-500">*</span>
                      </label>
                      {hasPrivilege(PRIVILEGES.CONFIG_VIEW) && (
                        <button
                          type="button"
                          onClick={() => setShowAddFacilityModal(true)}
                          disabled={isInactiveEdit}
                          title="Add a new facility"
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="material-symbols-outlined text-[14px]">
                            add
                          </span>
                          Add New Facility
                        </button>
                      )}
                    </div>
                    <Dropdown
                      options={facilities}
                      value={formData.facilityId}
                      onChange={handleFacilityChange}
                      placeholder={
                        formData.selectedDates.length === 0
                          ? "Select dates first"
                          : "Select a facility"
                      }
                      searchPlaceholder="Search facilitiesâ€¦"
                      disabled={
                        isInactiveEdit || formData.selectedDates.length === 0
                      }
                      error={errors.facilityId}
                    />
                  </div>

                  {/* Shift */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                        Shift Period <span className="text-red-500">*</span>
                      </label>
                      {/* Show the add-shift button only while fewer than 3 shifts exist (Morning / Afternoon / Night) and user has CONFIG_VIEW privilege. */}
                      {shifts.length < 3 &&
                        hasPrivilege(PRIVILEGES.CONFIG_VIEW) && (
                          <button
                            type="button"
                            onClick={() => setShowAddShiftModal(true)}
                            disabled={isInactiveEdit}
                            title="Add a new shift"
                            className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="material-symbols-outlined text-[14px]">
                              add
                            </span>
                            Add New Shift
                          </button>
                        )}
                    </div>
                    {shifts.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-6 px-4 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 text-center">
                        <span className="material-symbols-outlined text-slate-300 text-[36px] mb-2">
                          schedule
                        </span>
                        <p className="text-sm font-semibold text-slate-500">
                          No shifts configured yet
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Click <strong>Add New Shift</strong> above to create
                          one
                        </p>
                      </div>
                    ) : (
                      <>
                        {!formData.facilityId && (
                          <p className="text-xs text-slate-400 mb-2">
                            Select a facility to choose a shift.
                          </p>
                        )}
                        {bookedShiftsLoading && formData.facilityId && (
                          <p className="text-xs text-slate-400 mb-2">
                            Checking shift availabilityâ€¦
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {shifts.map((shift) => {
                            const isBooked = bookedShiftIds.includes(
                              shift.value,
                            );
                            const isDisabled =
                              isInactiveEdit ||
                              !formData.facilityId ||
                              isBooked;
                            return (
                              <label
                                key={shift.value}
                                className={`relative group ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                title={
                                  isBooked
                                    ? "This shift is already mapped for the selected dates and facility"
                                    : ""
                                }
                              >
                                <input
                                  type="radio"
                                  name="shift"
                                  value={shift.value}
                                  checked={
                                    parseInt(formData.shiftId) === shift.value
                                  }
                                  onChange={handleShiftChange}
                                  disabled={isDisabled}
                                  className="peer sr-only"
                                />
                                <div
                                  className={`p-3 border-2 rounded-xl transition-all relative
                            ${
                              isBooked
                                ? "border-slate-200 bg-slate-100 opacity-50"
                                : "border-transparent bg-slate-50 peer-checked:border-primary peer-checked:bg-primary/5 group-hover:bg-slate-100"
                            }`}
                                >
                                  <div className="flex flex-col items-center text-center">
                                    <span className="material-symbols-outlined mb-1 text-primary text-2xl">
                                      {shift.Shift_Name?.toLowerCase().includes(
                                        "morning",
                                      ) ||
                                      shift.Shift_Start_Time?.startsWith(
                                        "06",
                                      ) ||
                                      shift.Shift_Start_Time?.startsWith("07")
                                        ? "light_mode"
                                        : shift.Shift_Name?.toLowerCase().includes(
                                              "night",
                                            ) ||
                                            shift.Shift_Start_Time?.startsWith(
                                              "22",
                                            ) ||
                                            shift.Shift_Start_Time?.startsWith(
                                              "23",
                                            )
                                          ? "dark_mode"
                                          : "schedule"}
                                    </span>
                                    <p className="font-semibold text-sm">
                                      {shift.Shift_Name}
                                    </p>
                                    <p className="text-[10px] text-slate-500">
                                      {to12h(shift.Shift_Start_Time)} –{" "}
                                      {to12h(shift.Shift_End_Time)}
                                    </p>
                                    {isBooked && (
                                      <span className="mt-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
                                        Already Mapped
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {errors.shiftId && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.shiftId}
                      </p>
                    )}
                  </div>

                  {/* Employee - only meaningful after shift is selected */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                        Employee Name <span className="text-red-500">*</span>
                      </label>
                      {hasPrivilege(PRIVILEGES.EMP_CREATE) && (
                        <button
                          type="button"
                          onClick={() => setShowAddEmployeeModal(true)}
                          disabled={isInactiveEdit}
                          title="Add a new employee"
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="material-symbols-outlined text-[14px]">
                            add
                          </span>
                          Add New Employee
                        </button>
                      )}
                    </div>
                    {!formData.shiftId && (
                      <p className="text-xs text-slate-400 mb-2">
                        Select a shift to see available employees.
                      </p>
                    )}
                    <Dropdown
                      options={availableEmployees}
                      value={formData.employeeId}
                      onChange={handleEmployeeChange}
                      placeholder={
                        employeesLoading
                          ? "Loading available employees"
                          : !formData.shiftId
                            ? "Select a shift first"
                            : availableEmployees.length === 0
                              ? "No available employees for this selection"
                              : "Select an employee"
                      }
                      searchPlaceholder="Search employees"
                      disabled={
                        isInactiveEdit || !formData.shiftId || employeesLoading
                      }
                      error={errors.employeeId}
                    />
                  </div>
                </div>
              </FormCard>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-span-12 lg:col-span-4 space-y-3">
            {/* Visual Helper Card */}
            <div className="relative overflow-hidden rounded-xl h-48 bg-primary p-6 shadow-lg">
              <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-black/10 rounded-full blur-2xl"></div>
              <div className="relative z-10 h-full flex flex-col justify-end">
                <h4 className="text-white font-bold text-sm mb-1">
                  Conflict Validation
                </h4>
                <p className="text-white/70 text-[11px] leading-snug">
                  System ensures employees are not double-booked for the same
                  time period.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-slate-200">
          <button
            onClick={() => navigate("/shift-mapping")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">
              arrow_back
            </span>
            Back to List
          </button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            <span className="material-symbols-outlined text-lg">
              {saving ? "hourglass_empty" : "save"}
            </span>
            {saving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update Mapping"
                : "Save Mapping"}
          </Button>
        </div>
      </div>

      {/* -- Quick Add Shift - only when fewer than 3 shifts exist -- */}
      <ShiftDrawer
        isOpen={showAddShiftModal}
        onClose={() => setShowAddShiftModal(false)}
        onSave={handleShiftDrawerSave}
        isSaving={shiftSaving}
        saveError={shiftSaveError}
        editRow={null}
        existingCount={existingShiftRows.length}
        lastEndTime={lastShiftEndTime}
        existingRows={existingShiftRows}
      />

      {/* -- Add Employee - full Associate create page in a popup -- */}
      <QuickAddEmployeeModal
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onCreated={(newEmpId, newEmpName) =>
          refreshEmployeesAndSelect(newEmpId, newEmpName)
        }
      />

      {/* -- Add Facility - full Facility create page in a popup -- */}
      {showAddFacilityModal && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200]"
            onClick={() => setShowAddFacilityModal(false)}
          />
          <div className="fixed inset-0 z-[210] overflow-y-auto">
            <div className="min-h-full flex items-start justify-center p-4">
              <div
                className="bg-[#f7fafa] rounded-2xl shadow-2xl w-full max-w-6xl my-4 overflow-hidden relative"
                style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
                <div className="absolute top-4 right-4 z-10 flex gap-2">
                  <button
                    onClick={() => setShowAddFacilityModal(false)}
                    title="Close"
                    className="h-9 w-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-[#08757C] hover:border-[#08757C]/40 transition-all"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      close
                    </span>
                  </button>
                </div>
                <FacilityConfigCreate
                  embedded
                  onCancel={() => setShowAddFacilityModal(false)}
                  onSuccess={(newFacilityId, facilityName) => {
                    refreshFacilitiesAndSelect(newFacilityId, facilityName);
                    setShowAddFacilityModal(false);
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Employee Conflict Dialog */}
      <ConfirmDialog
        isOpen={employeeConflictDialog.open}
        title="Associate Already Mapped"
        message={`Associate ${employeeConflictDialog.employeeName} is already mapped for ${employeeConflictDialog.shiftCount} shift(s) in ${employeeConflictDialog.facilityName}. Do you want to assign them to another shift?`}
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={handleConfirmEmployeeConflict}
        onCancel={handleCancelEmployeeConflict}
        variant="warning"
      />
    </DashboardLayout>
  );
};

export default CreateEditShiftMappingPage;
